(function () {
  'use strict';

  const SELECTORS = {
    grid: '[data-wishlist-grid]',
    empty: '[data-wishlist-empty]',
    loading: '[data-wishlist-loading]',
    config: '[data-wishlist-config]',
    wishlistBtn: '[data-wishlist-btn]',
    productForm: '[data-product-form]',
    addToCart: '[data-add-to-cart]',
    buyNow: '[data-buy-now]',
  };

  const DEFAULTS = {
    icons: { heartOutline: '', heartFilled: '', error: '', spinner: '' },
    strings: {
      addToCart: 'Add to cart',
      soldOut: 'Sold out',
      buyNow: 'Buy Now',
    },
    endpoints: {
      products: '/collections/all/products.json',
      cartAdd: '/cart/add.js',
      checkout: '/checkout',
    },
    pagination: { pageSize: 250, maxPages: 10 },
  };

  const config = readConfig();

  document.addEventListener('DOMContentLoaded', init);

  async function init() {
    const manager = window.wishlistManager;
    if (!manager) return;
    await manager._ready;

    const grid = document.querySelector(SELECTORS.grid);
    const empty = document.querySelector(SELECTORS.empty);
    const loading = document.querySelector(SELECTORS.loading);

    const productIds = manager.getAll();
    loading.style.display = 'none';

    if (productIds.length === 0) {
      showEmpty(empty);
      return;
    }

    await renderWishlistProducts(productIds, grid, empty);
  }

  // ---------- Orchestration ----------

  async function renderWishlistProducts(productIds, grid, empty) {
    if (productIds.length === 0) {
      showEmpty(empty);
      return;
    }

    let fetchSucceeded = false;
    let wishlistProducts = [];
    let staleIds = [];

    try {
      ({ products: wishlistProducts, staleIds } =
        await window.wishlistManager._fetchProductsByIds(productIds));
      fetchSucceeded = true;
      console.log('fetched:', wishlistProducts, staleIds);
    } catch (err) {
      console.error('[Wishlist] Storefront fetch failed', err);
      return;
    }

    if (fetchSucceeded && staleIds.length > 0)
      await window.wishlistManager.reconcile(staleIds);

    if (wishlistProducts.length === 0) {
      showEmpty(empty);
      return;
    }

    grid.innerHTML = renderWishlistCards(wishlistProducts);
    bindWishlistButtons(grid, empty);
    bindAddToCartForms(grid);
    bindBuyNowButtons(grid);
  }

  // ---------- Rendering ----------

  function renderWishlistCards(products) {
    return products.map(renderWishlistCard).join('');
  }

  function renderWishlistCard(product) {
    const productId = product.id.split('/').at(-1);
    const image = product.featuredImage?.url || '';
    const variantId = product.variants.nodes[0]?.id || '';
    const available = product.variants.nodes[0]?.availableForSale ?? false;
    const { icons, strings } = config;

    return `
      <div class="card-product-hover" data-product-id="${productId}">
        <div class="card-product-hover__media">
          <a href="/products/${product.handle}" class="wishlist-card__image">
            <img src="${image}" alt="${product.title}" loading="lazy" width="400" height="400">
          </a>
          <button
            type="button"
            class="wishlist-btn is-wishlisted"
            data-wishlist-btn
            data-product-id="${productId}"
            aria-label="Remove from Wishlist"
          >
            <span class="icon-heart-outline">${icons.heartOutline}</span>
            <span class="icon-heart-filled" style="color:#D92225;">${icons.heartFilled}</span>
          </button>
          <product-form class="product-form" data-hide-errors="false">
            <div class="product-form__error-message-wrapper" role="alert" hidden>
              <span class="svg-wrapper">${icons.error}</span>
              <span class="product-form__error-message"></span>
            </div>
            <form
              class="form"
              novalidate="novalidate"
              data-type="add-to-cart-form"
              data-product-form
              data-variant-id="${variantId}"
            >
              <input type="hidden" name="id" value="${variantId}">
              <div
                class="product-form__buttons${available ? '' : ' variant-unavailable'}"
                data-product-buttons
              >
                <div class="buttons-available">
                  <button
                    type="submit"
                    name="add"
                    class="product-form__cart-button button button--secondary"
                    data-add-to-cart
                  >
                    <span class="btn-label">${strings.addToCart}</span>
                    <span class="sold-out-message hidden">${strings.soldOut}</span>
                    <div class="loading__spinner hidden">
                      <span class="svg-wrapper">${icons.spinner}</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    class="product-form__buy-now-button button button--primary"
                    data-buy-now
                  >
                    <span class="btn-label">${strings.buyNow}</span>
                    <div class="loading__spinner hidden">
                      <span class="svg-wrapper">${icons.spinner}</span>
                    </div>
                  </button>
                </div>
              </div>
            </form>
          </product-form>
        </div>
        <h3 class="card-product-hover__title">
          <a href="/products/${product.handle}">${product.title}</a>
        </h3>
        <a href="/products/${product.handle}" class="card-product-hover__link button button--tertiary">Learn More</a>
      </div>
    `;
  }

  // ---------- Data ----------

  async function fetchAllProducts() {
    const { pageSize, maxPages } = config.pagination;
    const endpoint = config.endpoints.products;
    const products = [];

    for (let page = 1; page <= maxPages; page++) {
      try {
        const res = await fetch(`${endpoint}?limit=${pageSize}&page=${page}`);
        const data = await res.json();
        products.push(...data.products);
        if (data.products.length < pageSize) break;
      } catch {
        break;
      }
    }
    return products;
  }

  async function addToCart(variantId) {
    const id = variantId.split('/').at(-1);
    if (!id) throw Error(`Invalid variant ID: ${variantId}`);
    return fetch(config.endpoints.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id, quantity: 1 }],
      }),
    });
  }

  // ---------- Bindings ----------

  function bindWishlistButtons(grid, empty) {
    grid.querySelectorAll(SELECTORS.wishlistBtn).forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await window.wishlistManager.toggle(btn.dataset.productId);
        // NOTE: original code targets `.wishlist-card`, which the rendered card doesn't have.
        // Preserved as-is to keep behavior identical; switch to `.card-product-hover` if you want removal to actually work.
        btn.closest('.wishlist-card')?.remove();
        if (grid.children.length === 0) showEmpty(empty);
      });
    });
  }

  function bindAddToCartForms(grid) {
    grid.querySelectorAll(SELECTORS.productForm).forEach((form) => {
      form.addEventListener('submit', (e) => handleAddToCartSubmit(e, form));
    });
  }

  async function handleAddToCartSubmit(e, form) {
    e.preventDefault();
    const variantId = form.dataset.variantId;
    if (!variantId) return;

    const btn = form.querySelector(SELECTORS.addToCart);
    const label = btn.querySelector('.btn-label');
    const originalText = label.textContent;

    btn.disabled = true;
    label.textContent = 'Adding...';

    try {
      await addToCart(variantId);
      label.textContent = 'Added!';
      setTimeout(() => {
        label.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    } catch {
      label.textContent = originalText;
      btn.disabled = false;
    }
  }

  function bindBuyNowButtons(grid) {
    grid.querySelectorAll(SELECTORS.buyNow).forEach((btn) => {
      btn.addEventListener('click', (e) => handleBuyNowClick(e, btn));
    });
  }

  async function handleBuyNowClick(e, btn) {
    e.preventDefault();
    const form = btn.closest(SELECTORS.productForm);
    const variantId = form?.dataset.variantId;
    if (!variantId) return;

    const label = btn.querySelector('.btn-label');
    const originalText = label.textContent;

    btn.disabled = true;
    label.textContent = 'Processing...';

    try {
      await addToCart(variantId);
      window.location.href = config.endpoints.checkout;
    } catch {
      label.textContent = originalText;
      btn.disabled = false;
    }
  }

  // ---------- Helpers ----------

  function readConfig() {
    const el = document.querySelector(SELECTORS.config);
    if (!el) return DEFAULTS;
    try {
      const parsed = JSON.parse(el.textContent);
      return {
        icons: { ...DEFAULTS.icons, ...(parsed.icons || {}) },
        strings: { ...DEFAULTS.strings, ...(parsed.strings || {}) },
        endpoints: { ...DEFAULTS.endpoints, ...(parsed.endpoints || {}) },
        pagination: { ...DEFAULTS.pagination, ...(parsed.pagination || {}) },
      };
    } catch (err) {
      console.error('[wishlist-page] config parse error', err);
      return DEFAULTS;
    }
  }

  function showEmpty(empty) {
    empty.style.display = '';
  }
})();
